<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="handleBackdropClick"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              标签管理
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              支持分组和主子账户互通
            </p>
          </div>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Tab 导航 -->
        <div class="border-b border-gray-200 mb-6">
          <nav class="-mb-px flex space-x-4 sm:space-x-8">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'py-2 px-2 sm:px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-1 sm:flex-initial',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <!-- 标签分组管理 Tab -->
        <div v-show="activeTab === 'groups'" class="space-y-6">
          <!-- 添加新分组 -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <label class="block text-sm font-medium text-gray-700 mb-3">创建新分组</label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <input
                  v-model="newGroup.name"
                  type="text"
                  placeholder="分组名称"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  @keyup.enter="handleAddGroup"
                />
              </div>
              <div>
                <input
                  v-model="newGroup.description"
                  type="text"
                  placeholder="分组描述（可选）"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>
            <div class="mt-4">
              <button
                @click="handleAddGroup"
                :disabled="!newGroup.name.trim() || addingGroup"
                class="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ addingGroup ? '添加中...' : '添加分组' }}
              </button>
            </div>
          </div>

          <!-- 分组列表 -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                已有分组 
                <span class="text-sm text-gray-500">({{ filteredTagGroups.length }}{{ groupSearchQuery ? ' / ' + tagGroups.length : '' }})</span>
              </label>
              <div class="relative">
                <input
                  v-model="groupSearchQuery"
                  type="text"
                  placeholder="搜索分组..."
                  class="w-32 sm:w-48 h-8 pl-8 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            <div v-if="loadingGroups" class="flex items-center justify-center py-8">
              <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="filteredTagGroups.length > 0" class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 max-h-[30vh] overflow-y-auto pr-2 -mr-2 items-start content-start" style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
              <div
                v-for="group in filteredTagGroups"
                :key="group.id"
                class="bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-200 hover:shadow-sm transition-all duration-200"
              >
                <div class="flex items-center justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-2">
                      <h4 class="text-sm font-medium text-gray-900 truncate" :title="group.description">
                        {{ group.name }}
                      </h4>
                      <span class="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{{ group.tag_count }}</span>
                    </div>
                  </div>
                  <div v-if="group.can_edit" class="flex items-center space-x-1 ml-2 flex-shrink-0">
                    <button
                      @click="editGroup(group)"
                      class="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs font-medium px-1.5 py-1 rounded transition-colors"
                    >
                      编辑
                    </button>
                    <button
                      @click="deleteGroup(group.id)"
                      class="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs font-medium px-1.5 py-1 rounded transition-colors"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8 flex flex-col items-center justify-center">
              <FolderIcon class="mx-auto h-12 w-12 text-gray-400" />
              <p class="mt-2 text-sm text-gray-500">
                {{ groupSearchQuery ? '没有找到匹配的分组' : '暂无分组' }}
              </p>
              <p v-if="groupSearchQuery" class="mt-1 text-xs text-gray-400">
                尝试修改搜索关键词
              </p>
            </div>
          </div>
        </div>

        <!-- 标签管理 Tab -->
        <div v-show="activeTab === 'tags'" class="space-y-6">
          <!-- 添加新标签 -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <label class="block text-sm font-medium text-gray-700 mb-3">创建新标签</label>
            <div class="space-y-4">
              <!-- 基本信息 -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input
                    v-model="newTag.name"
                    type="text"
                    placeholder="标签名称"
                    class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    @keyup.enter="handleAddTag"
                  />
                </div>
                <div>
                  <SearchableSelect
                    v-model="newTag.group"
                    :options="groupOptions"
                    placeholder="选择分组"
                    label-key="label"
                    value-key="value"
                  />
                </div>
              </div>

              <!-- 颜色选择和预览 -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- 背景颜色 -->
                <div>
                  <label class="block text-xs font-medium text-gray-700 mb-2">背景颜色</label>
                  <div class="flex flex-wrap gap-1">
                    <button
                      v-for="color in backgroundColors"
                      :key="color.value"
                      @click="newTag.backgroundColor = color.value"
                      :class="[
                        'w-6 h-6 rounded border-2 transition-all',
                        newTag.backgroundColor === color.value ? 'border-gray-900 ring-1 ring-blue-500' : 'border-gray-300'
                      ]"
                      :style="{ backgroundColor: color.value }"
                      :title="color.name"
                    />
                  </div>
                </div>
                
                <!-- 文字颜色和预览 -->
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-2">文字颜色</label>
                    <div class="flex flex-wrap gap-1">
                      <button
                        v-for="color in textColors"
                        :key="color.value"
                        @click="newTag.textColor = color.value"
                        :class="[
                          'w-6 h-6 rounded border-2 transition-all',
                          newTag.textColor === color.value ? 'border-gray-900 ring-1 ring-blue-500' : 'border-gray-300'
                        ]"
                        :style="{ backgroundColor: color.value }"
                        :title="color.name"
                      />
                    </div>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 mb-2">预览</label>
                    <div>
                      <span
                        v-if="newTag.name"
                        :style="{ 
                          backgroundColor: newTag.backgroundColor, 
                          color: newTag.textColor 
                        }"
                        class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
                      >
                        {{ newTag.name }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 添加按钮 -->
              <div>
                <button
                  @click="handleAddTag"
                  :disabled="!newTag.name.trim() || addingTag"
                  class="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {{ addingTag ? '添加中...' : '添加标签' }}
                </button>
              </div>

              <!-- 共享设置 - 🔧 已移除，默认所有标签都是共享的 -->
            </div>
          </div>

          <!-- 标签列表 (按分组显示) -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">
                已有标签 
                <span class="text-sm text-gray-500">({{ filteredTags.length }}{{ tagSearchQuery ? ' / ' + tags.length : '' }})</span>
              </label>
              <div class="relative">
                <input
                  v-model="tagSearchQuery"
                  type="text"
                  placeholder="搜索标签..."
                  class="w-32 sm:w-48 h-8 pl-8 pr-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <svg class="absolute left-2.5 top-2 h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m21 21-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div v-if="loadingTags" class="flex items-center justify-center py-8">
              <svg class="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 004 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="groupedTags.length > 0" class="space-y-4 max-h-[30vh] overflow-y-auto pr-2 -mr-2" style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
              <!-- 分组显示标签 -->
              <div 
                v-for="group in groupedTags"
                :key="group.id || 'ungrouped'"
              >
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center space-x-2">
                    <h4 class="text-sm font-medium text-gray-900">
                      {{ group.name || '未分组' }}
                    </h4>
                    <span class="text-xs text-gray-500">({{ group.tags.length }} 个标签)</span>
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  <div
                    v-for="tag in group.tags"
                    :key="tag.id"
                    class="flex items-center justify-between p-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors min-h-[40px]"
                  >
                    <!-- 标签显示 -->
                    <div class="flex items-center space-x-1 sm:space-x-2 min-w-0 flex-1 overflow-hidden">
                      <span
                        :style="{ 
                          backgroundColor: tag.background_color || '#3B82F6', 
                          color: tag.text_color || '#FFFFFF' 
                        }"
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 max-w-[120px] sm:max-w-none overflow-hidden"
                        :title="tag.name"
                      >
                        <span class="truncate">{{ tag.name }}</span>
                      </span>
                      <!--<span class="text-xs text-gray-500 flex-shrink-0">
                        {{ tag.customer_count || 0 }}
                      </span>--> 
                      <!-- 权限标识 - 🔧 已移除，所有标签都是共享的 -->
                      <div class="flex items-center space-x-1 flex-shrink-0">
                        <span 
                          v-if="tag.creator_info && tag.creator_info.account_type === 'sub'"
                          class="inline-flex items-center px-1 py-0.5 text-xs text-purple-600 bg-purple-100 rounded"
                          :title="`子账户 ${tag.creator_info.username} 创建`"
                        >
                          👤
                        </span>
                        <span 
                          v-if="tag.creator_info && tag.creator_info.account_type === 'master'"
                          class="inline-flex items-center px-1 py-0.5 text-xs text-green-600 bg-green-100 rounded"
                          :title="`主账户 ${tag.creator_info.username} 创建`"
                        >
                        </span>
                      </div>
                    </div>

                    <!-- 操作按钮 -->
                    <div class="flex items-center space-x-1 ml-2 flex-shrink-0">
                      <button
                        v-if="tag.can_edit"
                        @click="editTag(tag)"
                        class="text-blue-600 hover:text-blue-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center"
                        title="编辑标签"
                      >
                        ✏️
                      </button>
                      <button
                        v-if="tag.can_delete"
                        @click="deleteTag(tag.id)"
                        class="text-red-600 hover:text-red-800 text-xs font-medium px-1 h-6 w-6 flex items-center justify-center"
                        title="删除标签"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="text-center py-8 flex flex-col items-center justify-center">
              <TagIcon class="mx-auto h-12 w-12 text-gray-400" />
              <p class="mt-2 text-sm text-gray-500">
                {{ tagSearchQuery ? '没有找到匹配的标签' : '暂无标签' }}
              </p>
              <p v-if="tagSearchQuery" class="mt-1 text-xs text-gray-400">
                尝试修改搜索关键词
              </p>
            </div>
          </div>
        </div>

        <!-- 编辑分组模态框 -->
        <div
          v-if="editingGroup"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10"
          @click="cancelEditGroup"
        >
          <div 
            class="bg-white rounded-lg p-6 w-[90vw] max-w-md mx-4"
            @click.stop
          >
            <h4 class="text-lg font-medium text-gray-900 mb-4">编辑分组</h4>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">分组名称</label>
                <input
                  v-model="editingGroup.name"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">分组描述</label>
                <input
                  v-model="editingGroup.description"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button
                @click="cancelEditGroup"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                @click="handleUpdateGroup"
                :disabled="updatingGroup"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {{ updatingGroup ? '保存中...' : '保存' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 编辑标签模态框 -->
        <div
          v-if="editingTag"
          class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10"
          @click="cancelEditTag"
        >
          <div 
            class="bg-white rounded-lg p-6 w-[90vw] max-w-md mx-4"
            @click.stop
          >
            <h4 class="text-lg font-medium text-gray-900 mb-4">编辑标签</h4>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">标签名称</label>
                <input
                  v-model="editingTag.name"
                  type="text"
                  class="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">所属分组</label>
                <SearchableSelect
                  v-model="editingTag.group"
                  :options="groupOptions"
                  placeholder="选择分组"
                  label-key="label"
                  value-key="value"
                />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">背景颜色</label>
                  <input
                    v-model="editingTag.background_color"
                    type="color"
                    class="w-full h-10 cursor-pointer"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">文字颜色</label>
                  <input
                    v-model="editingTag.text_color"
                    type="color"
                    class="w-full h-10 cursor-pointer"
                  />
                </div>
              </div>
              <!-- 共享设置 - 🔧 已移除，默认所有标签都是共享的 -->
              <div v-if="editingTag.name" class="flex items-center space-x-2">
                <span class="text-sm text-gray-700">预览:</span>
                <span
                  :style="{ 
                    backgroundColor: editingTag.background_color, 
                    color: editingTag.text_color 
                  }"
                  class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
                >
                  {{ editingTag.name }}
                </span>
              </div>
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button
                @click="cancelEditTag"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                @click="handleUpdateTag"
                :disabled="updatingTag"
                class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {{ updatingTag ? '保存中...' : '保存' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 底部操作按钮 -->
        <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { customerTagService, customerTagGroupService } from '@/services/customerService'
import { XMarkIcon, FolderIcon, TagIcon } from '@heroicons/vue/24/outline'
import SearchableSelect from '@/components/common/SearchableSelect.vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'success'])

const toast = useToast()

// Tab 状态
const activeTab = ref('tags')
const tabs = [
  { id: 'tags', name: '标签管理' },
  { id: 'groups', name: '分组管理' }
]

// 数据状态
const tags = ref([])
const tagGroups = ref([])
const loadingTags = ref(false)
const loadingGroups = ref(false)

// 搜索状态
const groupSearchQuery = ref('')
const tagSearchQuery = ref('')

// 计算属性
const filteredTagGroups = computed(() => {
  if (!groupSearchQuery.value.trim()) {
    return tagGroups.value
  }
  
  const query = groupSearchQuery.value.toLowerCase()
  return tagGroups.value.filter(group => 
    group.name.toLowerCase().includes(query) ||
    (group.description && group.description.toLowerCase().includes(query))
  )
})

const filteredTags = computed(() => {
  if (!tagSearchQuery.value.trim()) {
    return tags.value
  }
  
  const query = tagSearchQuery.value.toLowerCase()
  return tags.value.filter(tag => 
    tag.name.toLowerCase().includes(query) ||
    (tag.description && tag.description.toLowerCase().includes(query))
  )
})

const groupOptions = computed(() => {
  const options = tagGroups.value.map(group => ({
    label: group.name,
    value: group.id
  }))
  
  // 添加"无分组"选项到开头
  return [
    { label: '无分组', value: null },
    ...options
  ]
})

// 新增分组
const newGroup = reactive({
  name: '',
  description: ''
})
const addingGroup = ref(false)

// 新增标签
const newTag = reactive({
  name: '',
  group: null,
  backgroundColor: '#3B82F6',
  textColor: '#FFFFFF',
  isShared: true
})
const addingTag = ref(false)

// 编辑状态
const editingGroup = ref(null)
const editingTag = ref(null)
const updatingGroup = ref(false)
const updatingTag = ref(false)

// 颜色选项
const backgroundColors = [
  { name: '蓝色', value: '#3b82f6' },
  { name: '绿色', value: '#10b981' },
  { name: '黄色', value: '#f59e0b' },
  { name: '红色', value: '#ef4444' },
  { name: '紫色', value: '#8b5cf6' },
  { name: '橙色', value: '#f97316' },
  { name: '青色', value: '#06b6d4' },
  { name: '粉色', value: '#ec4899' },
  { name: '灰色', value: '#6b7280' }
]

const textColors = [
  { name: '白色', value: '#ffffff' },
  { name: '黑色', value: '#000000' },
  { name: '深灰', value: '#374151' }
]

// 计算属性
const groupedTags = computed(() => {
  const groups = [...tagGroups.value]
  
  // 为每个分组添加标签
  groups.forEach(group => {
    group.tags = filteredTags.value.filter(tag => tag.group_info?.id === group.id)
  })
  
  // 添加未分组的标签
  const ungroupedTags = filteredTags.value.filter(tag => !tag.group_info)
  if (ungroupedTags.length > 0) {
    groups.push({
      id: null,
      name: '未分组',
      color: null,
      tags: ungroupedTags
    })
  }
  
  return groups.filter(group => group.tags.length > 0)
})

// 方法
const handleBackdropClick = () => {
  emit('close')
}

// 加载数据
const loadTagGroups = async () => {
  loadingGroups.value = true
  try {
    const result = await customerTagGroupService.getTagGroups()
    if (result.success) {
      tagGroups.value = result.data.results || []
    } else {
      toast.error(result.message || '加载分组失败')
    }
  } catch (error) {
    console.error('加载分组失败:', error)
    toast.error('加载分组失败')
  } finally {
    loadingGroups.value = false
  }
}

const loadTags = async () => {
  loadingTags.value = true
  try {
    const result = await customerTagService.getTags()
    if (result.success) {
      tags.value = result.data.results || []
    } else {
      toast.error(result.message || '加载标签失败')
    }
  } catch (error) {
    console.error('加载标签失败:', error)
    toast.error('加载标签失败')
  } finally {
    loadingTags.value = false
  }
}

// 分组操作
const handleAddGroup = async () => {
  if (!newGroup.name.trim()) {
    toast.warning('请输入分组名称')
    return
  }

  addingGroup.value = true
  try {
    const result = await customerTagGroupService.createTagGroup({
      name: newGroup.name,
      description: newGroup.description
    })

    if (result.success) {
      toast.success('分组创建成功')
      tagGroups.value.unshift(result.data)
      // 重置表单
      newGroup.name = ''
      newGroup.description = ''
    } else {
      toast.error(result.message || '创建分组失败')
    }
  } catch (error) {
    console.error('创建分组失败:', error)
    toast.error('创建分组失败')
  } finally {
    addingGroup.value = false
  }
}

const editGroup = (group) => {
  editingGroup.value = { ...group }
}

const cancelEditGroup = () => {
  editingGroup.value = null
}

const handleUpdateGroup = async () => {
  if (!editingGroup.value.name.trim()) {
    toast.warning('请输入分组名称')
    return
  }

  updatingGroup.value = true
  try {
    const result = await customerTagGroupService.updateTagGroup(editingGroup.value.id, {
      name: editingGroup.value.name,
      description: editingGroup.value.description
    })

    if (result.success) {
      toast.success('分组更新成功')
      const index = tagGroups.value.findIndex(g => g.id === editingGroup.value.id)
      if (index !== -1) {
        tagGroups.value[index] = result.data
      }
      editingGroup.value = null
    } else {
      toast.error(result.message || '更新分组失败')
    }
  } catch (error) {
    console.error('更新分组失败:', error)
    toast.error('更新分组失败')
  } finally {
    updatingGroup.value = false
  }
}

const deleteGroup = async (groupId) => {
  if (!confirm('确定要删除此分组吗？删除后分组下的标签将变为未分组状态。')) {
    return
  }

  try {
    const result = await customerTagGroupService.deleteTagGroup(groupId)
    if (result.success) {
      toast.success('分组删除成功')
      tagGroups.value = tagGroups.value.filter(g => g.id !== groupId)
      // 重新加载标签以更新分组信息
      await loadTags()
    } else {
      toast.error(result.message || '删除分组失败')
    }
  } catch (error) {
    console.error('删除分组失败:', error)
    toast.error('删除分组失败')
  }
}

// 标签操作
const handleAddTag = async () => {
  if (!newTag.name.trim()) {
    toast.warning('请输入标签名称')
    return
  }

  addingTag.value = true
  try {
    const result = await customerTagService.createTag({
      name: newTag.name,
      group: newTag.group,
      background_color: newTag.backgroundColor,
      text_color: newTag.textColor,
      is_shared: newTag.isShared
    })

    if (result.success) {
      toast.success('标签创建成功')
      tags.value.unshift(result.data)
      // 重置表单
      newTag.name = ''
      newTag.group = null
      newTag.backgroundColor = '#3B82F6'
      newTag.textColor = '#FFFFFF'
      newTag.isShared = true
    } else {
      toast.error(result.message || '创建标签失败')
    }
  } catch (error) {
    console.error('创建标签失败:', error)
    toast.error('创建标签失败')
  } finally {
    addingTag.value = false
  }
}

const editTag = (tag) => {
  editingTag.value = { 
    ...tag,
    group: tag.group_info?.id || null
  }
}

const cancelEditTag = () => {
  editingTag.value = null
}

const handleUpdateTag = async () => {
  if (!editingTag.value.name.trim()) {
    toast.warning('请输入标签名称')
    return
  }

  updatingTag.value = true
  try {
    const result = await customerTagService.updateTag(editingTag.value.id, {
      name: editingTag.value.name,
      group: editingTag.value.group,
      background_color: editingTag.value.background_color,
      text_color: editingTag.value.text_color,
      is_shared: true  // 🔧 强制所有标签都是共享的
    })

    if (result.success) {
      toast.success('标签更新成功')
      const index = tags.value.findIndex(t => t.id === editingTag.value.id)
      if (index !== -1) {
        tags.value[index] = result.data
      }
      editingTag.value = null
    } else {
      toast.error(result.message || '更新标签失败')
    }
  } catch (error) {
    console.error('更新标签失败:', error)
    toast.error('更新标签失败')
  } finally {
    updatingTag.value = false
  }
}

const deleteTag = async (tagId) => {
  if (!confirm('确定要删除此标签吗？此操作不可撤销。')) {
    return
  }

  try {
    const result = await customerTagService.deleteTag(tagId)
    if (result.success) {
      toast.success('标签删除成功')
      tags.value = tags.value.filter(t => t.id !== tagId)
    } else {
      toast.error(result.message || '删除标签失败')
    }
  } catch (error) {
    console.error('删除标签失败:', error)
    toast.error('删除标签失败')
  }
}

// 监听显示状态
watch(() => props.show, (newVal) => {
  if (newVal) {
    loadTagGroups()
    loadTags()
  }
})

onMounted(() => {
  if (props.show) {
    loadTagGroups()
    loadTags()
  }
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
